/**
 * 员工编辑对话框（identity-access:SC-43 + staff-availability:SC-06/SC-09~SC-12）。
 *
 * 三页签：基本信息 / 排课时段 / 临时不可排课日期。
 * - 基本信息：姓名 / 联系手机号 / 岗位（多选），提交 PATCH /staffs/:id。
 *   状态不在本表单修改（由列表停用/启用操作管理）；员工编号与微信绑定信息只读。
 * - 排课时段 / 临时不可排课日期：自 5-3 起真实生效——初始化自 StaffDetail 的
 *   weeklyAvailability/availabilityExceptions，保存为全量替换
 *   （PUT /staffs/:id/availability 与 /availability-exceptions，仅变更页签提交）；
 *   服务端校验失败（重叠/互斥/原因缺失）在对话框内提示且不关闭。
 */
import { useState } from 'react';
import { Button, Checkbox, DatePicker, Form, Input, Select, Space, Tabs, TimePicker, Typography } from 'antd';
import { message } from '../../../utils/antd-message';
import { DraggableModal } from '../../../components/DraggableModal';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import {
  staffApi,
  staffPositionLabel,
  weekdayLabel,
  staffStatusLabel,
  type AvailabilityException,
  type StaffDetail,
  type StaffStatus,
  type StaffPosition,
  type WeeklyAvailabilitySlot,
} from '../../../api/staff.api';
import { ApiError } from '../../../api/transport';

const { Text } = Typography;
const TIME_FORMAT = 'HH:mm';

/** 编辑页签类型 */
export type StaffEditTab = 'basic' | 'availability' | 'exceptions';

export interface StaffEditModalProps {
  /** 员工详情（含排课时段与临时不可排课日期，5-3 起后端返回） */
  staff: StaffDetail;
  /** 初始页签（详情抽屉跳转指定） */
  initialTab?: StaffEditTab;
  /** 关闭回调 */
  onClose: () => void;
  /** 保存成功回调（供父组件刷新） */
  onSaved?: () => void;
}

export function StaffEditModal({ staff, initialTab = 'basic', onClose, onSaved }: StaffEditModalProps) {
  const [form] = Form.useForm<{ name: string; mobile?: string; positions: StaffPosition[]; status: StaffStatus }>();
  const [submitting, setSubmitting] = useState(false);
  // 5-3：自详情初始化（id 保留用于对比变更，提交时剔除）
  const [slots, setSlots] = useState<WeeklyAvailabilitySlot[]>(staff.weeklyAvailability ?? []);
  const [exceptions, setExceptions] = useState<AvailabilityException[]>(
    staff.availabilityExceptions ?? [],
  );
  /** 初始快照：判断页签数据是否变更（未变更不提交，避免无谓触碰并发闸门）。 */
  const initialSlotsJson = JSON.stringify(staff.weeklyAvailability ?? []);
  const initialExceptionsJson = JSON.stringify(
    (staff.availabilityExceptions ?? []).map((e) => ({
      date: e.date,
      allDay: e.allDay,
      startTime: e.allDay ? null : (e.startTime ?? null),
      endTime: e.allDay ? null : (e.endTime ?? null),
      reason: e.reason ?? '',
    })),
  );

  // 岗位选项（从标签映射派生）
  const positionOptions = (Object.keys(staffPositionLabel) as StaffPosition[]).map((value) => ({
    value,
    label: staffPositionLabel[value],
  }));
  const weekdayOptions = Object.entries(weekdayLabel).map(([value, label]) => ({
    value: Number(value),
    label,
  }));

  function updateSlot(index: number, patch: Partial<WeeklyAvailabilitySlot>) {
    setSlots((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }

  function updateException(index: number, patch: Partial<AvailabilityException>) {
    setExceptions((prev) => prev.map((e, i) => (i === index ? { ...e, ...patch } : e)));
  }

  async function submit() {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      // 行完整性前置校验：缺项明示报错并整体中止保存（不静默丢弃、不出现半保存状态）
      // 排课时段页签：行完整性校验（缺项明示报错，不静默丢弃），仅变更时全量替换（staff-availability:SC-06）
      const incompleteSlotIndex = slots.findIndex(
        (s) => !s.dayOfWeek || !s.startTime || !s.endTime,
      );
      if (incompleteSlotIndex >= 0) {
        message.warning(`排课时段第 ${incompleteSlotIndex + 1} 行的星期或起止时间未填写完整`);
        return;
      }
      // 例外页签：行完整性校验（缺项明示报错），仅变更时全量替换（staff-availability:SC-09/SC-12）
      const incompleteExceptionIndex = exceptions.findIndex(
        (e) => !e.date || !(e.reason ?? '').trim() || (!e.allDay && (!e.startTime || !e.endTime)),
      );
      if (incompleteExceptionIndex >= 0) {
        message.warning(`临时不可排课日期第 ${incompleteExceptionIndex + 1} 行的日期/原因或时段起止未填写完整`);
        return;
      }
      // 基本信息提交（PATCH /staffs/:id）
      await staffApi.updateStaff(staff.id, {
        name: values.name,
        mobile: values.mobile,
        positions: values.positions,
        status: values.status,
      });
      const slotsJson = JSON.stringify(slots);
      if (slotsJson !== JSON.stringify(JSON.parse(initialSlotsJson))) {
        await staffApi.saveStaffAvailability(staff.id, {
          items: slots.map((s) => ({
            dayOfWeek: s.dayOfWeek,
            startTime: s.startTime,
            endTime: s.endTime,
          })),
        });
      }
      const validExceptions = exceptions.map((e) => ({
        date: e.date,
        allDay: e.allDay,
        startTime: e.allDay ? null : (e.startTime ?? null),
        endTime: e.allDay ? null : (e.endTime ?? null),
        reason: (e.reason ?? '').trim(),
      }));
      if (JSON.stringify(validExceptions) !== initialExceptionsJson) {
        await staffApi.saveStaffAvailabilityExceptions(staff.id, { items: validExceptions });
      }
      message.success('已保存员工信息');
      onSaved?.();
      onClose();
    } catch (err) {
      // 服务端校验失败（重叠/互斥/原因缺失）在对话框内提示且不关闭（identity-access:SC-43）
      if (err instanceof ApiError) message.error(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <DraggableModal
      open
      title={`编辑员工 · ${staff.name}`}
      onCancel={onClose}
      destroyOnHidden
      width={720}
      footer={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" loading={submitting} onClick={() => void submit()}>
            保存
          </Button>
        </Space>
      }
    >
      <Tabs
        defaultActiveKey={initialTab}
        items={[
          {
            key: 'basic',
            label: '基本信息',
            children: (
              <Form
                form={form}
                layout="vertical"
                initialValues={{
                  name: staff.name,
                  mobile: staff.mobile ?? undefined,
                  positions: staff.positions,
                  status: staff.status,
                }}
              >
                <Space size={16} wrap>
                  <Form.Item label="姓名" name="name" rules={[{ required: true, message: '请输入姓名' }]}>
                    <Input style={{ width: 160 }} />
                  </Form.Item>
                  <Form.Item
                    label="联系手机号"
                    name="mobile"
                    rules={[{ pattern: /^1\d{10}$|^\*+$/, message: '请输入正确的手机号' }]}
                  >
                    <Input style={{ width: 180 }} placeholder="11 位手机号" />
                  </Form.Item>
                  <Form.Item label="岗位" name="positions" rules={[{ required: true, message: '请选择岗位' }]}>
                    <Select mode="multiple" style={{ minWidth: 220 }} options={positionOptions} />
                  </Form.Item>
                  <Form.Item label="状态" name="status" rules={[{ required: true, message: '请选择状态' }]}>
                    <Select
                      style={{ width: 120 }}
                      options={(Object.keys(staffStatusLabel) as StaffStatus[]).map((value) => ({
                        value,
                        label: staffStatusLabel[value],
                      }))}
                    />
                  </Form.Item>
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  员工编号、微信绑定信息不可通过本表单修改；校区与角色授权请使用「授权」功能。
                </Text>
              </Form>
            ),
          },
          {
            key: 'availability',
            label: '排课时段',
            children: (
              <div>
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                  {slots.map((slot, index) => (
                    <Space key={index} wrap>
                      <Select
                        style={{ width: 100 }}
                        value={slot.dayOfWeek}
                        options={weekdayOptions}
                        onChange={(v) => updateSlot(index, { dayOfWeek: v })}
                      />
                      <TimePicker
                        format={TIME_FORMAT}
                        value={slot.startTime ? dayjs(slot.startTime, TIME_FORMAT) : null}
                        placeholder="开始时间"
                        onChange={(v: Dayjs | null) =>
                          updateSlot(index, { startTime: v ? v.format(TIME_FORMAT) : '' })
                        }
                      />
                      <span>至</span>
                      <TimePicker
                        format={TIME_FORMAT}
                        value={slot.endTime ? dayjs(slot.endTime, TIME_FORMAT) : null}
                        placeholder="结束时间"
                        onChange={(v: Dayjs | null) =>
                          updateSlot(index, { endTime: v ? v.format(TIME_FORMAT) : '' })
                        }
                      />
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => setSlots((prev) => prev.filter((_, i) => i !== index))}
                      />
                    </Space>
                  ))}
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() =>
                      setSlots((prev) => [...prev, { dayOfWeek: 1, startTime: '09:00', endTime: '18:00' }])
                    }
                  >
                    添加时段
                  </Button>
                </Space>
                <Text type="secondary" style={{ fontSize: 12, marginTop: 12, display: 'block' }}>
                  设置该员工每周可参与排课的时间段，排课工作台将按此校验冲突。
                </Text>
              </div>
            ),
          },
          {
            key: 'exceptions',
            label: '临时不可排课日期',
            children: (
              <div>
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                  {exceptions.map((ex, index) => (
                    <Space key={index} wrap>
                      <DatePicker
                        value={ex.date ? dayjs(ex.date) : null}
                        onChange={(v: Dayjs | null) =>
                          updateException(index, { date: v ? v.format('YYYY-MM-DD') : '' })
                        }
                      />
                      <Checkbox
                        checked={ex.allDay}
                        onChange={(e) => updateException(index, { allDay: e.target.checked })}
                      >
                        全天
                      </Checkbox>
                      {!ex.allDay && (
                        <>
                          <TimePicker
                            format={TIME_FORMAT}
                            value={ex.startTime ? dayjs(ex.startTime, TIME_FORMAT) : null}
                            placeholder="开始时间"
                            onChange={(v: Dayjs | null) =>
                              updateException(index, { startTime: v ? v.format(TIME_FORMAT) : undefined })
                            }
                          />
                          <span>至</span>
                          <TimePicker
                            format={TIME_FORMAT}
                            value={ex.endTime ? dayjs(ex.endTime, TIME_FORMAT) : null}
                            placeholder="结束时间"
                            onChange={(v: Dayjs | null) =>
                              updateException(index, { endTime: v ? v.format(TIME_FORMAT) : undefined })
                            }
                          />
                        </>
                      )}
                      <Input
                        style={{ width: 160 }}
                        placeholder="原因（如：事假）"
                        value={ex.reason ?? ''}
                        onChange={(e) => updateException(index, { reason: e.target.value })}
                      />
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => setExceptions((prev) => prev.filter((_, i) => i !== index))}
                      />
                    </Space>
                  ))}
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => setExceptions((prev) => [...prev, { date: '', allDay: true, reason: '' }])}
                  >
                    添加日期
                  </Button>
                </Space>
                <Text type="secondary" style={{ fontSize: 12, marginTop: 12, display: 'block' }}>
                  临时不可排课日期优先级高于每周排课时段，保存时需填写日期与原因。
                </Text>
              </div>
            ),
          },
        ]}
      />
    </DraggableModal>
  );
}
